"use client";

/**
 * A filter pill with a popover of options, in the shared filter-bar
 * style (DESIGN.md check 2/3): `h-9 rounded-lg border`, the label in
 * gray, a set filter white with the 2 px dark outline and its value
 * (lib/ui-classes SELECTED, a faint brand tint). The popover closes on an
 * outside click and on Escape.
 *
 * Same look as the AuctionsHub / LandMapView pills, built once here so
 * new filter bars reuse it instead of copying another local version.
 * `FilterChoice` is the phone-sheet form of the same options: a row of
 * pills under a label, the chosen one outlined.
 */
import { useEffect, useRef, type ReactNode } from "react";
import { Check, ChevronDown } from "lucide-react";
import { ROW_ON, SELECTED, UNSELECTED } from "@/lib/ui-classes";

export function FilterPill({
  label,
  value,
  active,
  open,
  onOpenChange,
  children,
  align = "left",
  className = "",
  disabled = false,
  title,
}: {
  label: string;
  value?: string | null;
  active: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
  align?: "left" | "right";
  className?: string;
  /** Greyed and closed: the choice does not apply right now. */
  disabled?: boolean;
  title?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent | TouchEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onOpenChange(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onOpenChange(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onOpenChange]);

  return (
    <div ref={ref} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => onOpenChange(!open)}
        aria-expanded={open}
        aria-haspopup="true"
        disabled={disabled}
        title={title}
        className={`inline-flex h-9 items-center gap-1.5 rounded-lg border px-3 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-50 ${
          active
            ? SELECTED
            : open
              ? "border-gray-300 bg-white text-gray-900"
              : UNSELECTED
        }`}
      >
        {active && value ? (
          <span className="max-w-[14rem] truncate">{value}</span>
        ) : (
          <>
            <span className={active ? "" : "text-gray-500"}>{label}</span>
            {value && <span className="max-w-[12rem] truncate">{value}</span>}
          </>
        )}
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} aria-hidden />
      </button>
      {open && !disabled && (
        <div
          className={`absolute top-full z-30 mt-1.5 w-64 rounded-xl border border-gray-200 bg-white p-2 shadow-[0_24px_64px_-24px_rgba(15,23,42,0.25)] animate-in fade-in zoom-in-95 duration-150 ${
            align === "right" ? "right-0 origin-top-right" : "left-0 origin-top-left"
          }`}
        >
          {children}
        </div>
      )}
    </div>
  );
}

export function FilterOption({
  on,
  onClick,
  children,
  meta,
  disabled = false,
  title,
}: {
  on: boolean;
  onClick: () => void;
  children: ReactNode;
  /** Right-aligned secondary text, e.g. a count. */
  meta?: ReactNode;
  disabled?: boolean;
  title?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={on}
      disabled={disabled}
      title={title}
      className={`flex w-full items-center justify-between gap-2 rounded-lg px-2.5 py-2 text-left text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-50 ${
        on ? ROW_ON : "text-gray-700 hover:bg-gray-50"
      }`}
    >
      <span className="min-w-0 truncate">{children}</span>
      <span className="flex shrink-0 items-center gap-1.5">
        {meta != null && (
          <span className={`text-xs tabular-nums ${on ? "text-gray-600" : "text-gray-400"}`}>{meta}</span>
        )}
        {on && <Check className="h-4 w-4" aria-hidden />}
      </span>
    </button>
  );
}

/** The same options as a labelled pill row, for a phone sheet. */
export function FilterChoice<T extends string | number>({
  label,
  options,
  value,
  onChange,
}: {
  label?: string;
  options: { v: T; label: ReactNode; disabled?: boolean; title?: string }[];
  /** null: nothing chosen (the choice does not apply right now). */
  value: T | null;
  onChange: (v: T) => void;
}) {
  return (
    <div>
      {label && <p className="mb-1.5 text-xs font-semibold text-gray-500">{label}</p>}
      <div role="group" aria-label={label} className="flex flex-wrap gap-1.5">
        {options.map((o) => {
          const on = o.v === value;
          return (
            <button
              key={String(o.v)}
              type="button"
              aria-pressed={on}
              disabled={o.disabled}
              title={o.title}
              onClick={() => onChange(o.v)}
              className={`inline-flex h-9 items-center gap-1 rounded-lg border px-3 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:opacity-50 ${
                on ? SELECTED : UNSELECTED
              }`}
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
